import { create } from 'zustand';
import api from '@/services/api';
import type { UserPreferences } from '@/types';

interface PreferencesStore {
  preferences: UserPreferences | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchPreferences: () => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  clearPreferences: () => void;
  
  // Getters
  enterToSend: () => boolean;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  default_tone: null,
  default_platforms: [],
  email_notifications: true,
  marketing_emails: false,
  enter_to_send: false,
  has_completed_tutorial: false,
  tutorial_dismissed_at: null,
};

export const usePreferencesStore = create<PreferencesStore>((set, get) => ({
  preferences: null,
  isLoading: false,
  error: null,
  
  fetchPreferences: async () => {
    // Don't fetch if already loading
    if (get().isLoading) return;
    
    set({ isLoading: true, error: null });
    try {
      const prefs = await api.getPreferences();
      set({ preferences: prefs, isLoading: false });
    } catch (err: any) {
      console.error('Failed to fetch preferences:', err);
      set({ error: 'Failed to load preferences', isLoading: false });
    }
  },
  
  updatePreferences: async (updates: Partial<UserPreferences>) => {
    try {
      const result = await api.updatePreferences(updates);
      set({ preferences: result });
    } catch (err: any) {
      console.error('Failed to update preferences:', err);
      throw err;
    }
  },
  
  // Getter for enter_to_send preference
  enterToSend: () => {
    const prefs = get().preferences;
    return prefs?.enter_to_send ?? DEFAULT_PREFERENCES.enter_to_send;
  },
  
  // Clear preferences on logout
  clearPreferences: () => {
    set({ preferences: null, isLoading: false, error: null });
  },
}));
